/**
 * Admin → Käyttäjähallinta page.
 *
 * Wraps `GET /api/v1/smt/admin/users[?status=]`, `PUT .../approve` and
 * `PUT .../reject`. The full CRUD modal from the mockup
 * (vertailutyokalu.html#editUserModal, role/level/regions editor) is M5
 * work; this view delivers the high-impact subset:
 *
 *   - tab filter (Pending / Active / All)
 *   - table with name, email, role, level, status, created_at
 *   - inline Approve / Reject buttons that call the existing endpoints
 *
 * Approve uses the user's currently-set level (defaults to 2, matching the
 * backend default). Region scoping for level=1 users is a TODO that will be
 * exposed via the full editor modal in M5.
 */
import { listUsers, approveUser, rejectUser, updateUser, deleteUser, type UserStatusFilter, type UpdateUserInput } from '../api/admin.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';
import { REGIONS } from '../data/regions.ts';
import type { User } from '../types/api.ts';

const TABS: UserStatusFilter[] = ['pending', 'active', 'all'];

let activeTab: UserStatusFilter = 'pending';

// Client-side sorting (asiakaspalaute 1.6: "paljon käyttäjiä → pitää sortata").
// Default mirrors the backend order (newest first).
type SortKey = 'name' | 'email' | 'role' | 'level' | 'status' | 'created';
const SORT_COLS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'view_admin_th_name' },
  { key: 'email', label: 'view_admin_th_email' },
  { key: 'role', label: 'view_admin_th_role' },
  { key: 'level', label: 'view_admin_th_level' },
  { key: 'status', label: 'view_admin_th_status' },
  { key: 'created', label: 'view_admin_th_created' },
];
let sortKey: SortKey = 'created';
let sortDir: 'asc' | 'desc' = 'desc';

function sortValue(u: User, key: SortKey): string | number {
  switch (key) {
    case 'name':
      return (u.name || `${u.first_name || ''} ${u.last_name || ''}`).trim().toLowerCase();
    case 'email':
      return (u.email || '').toLowerCase();
    case 'role':
      return (u.role || 'user').toLowerCase();
    case 'level':
      return u.level || 0;
    case 'status':
      return (u.status || 'active').toLowerCase();
    case 'created':
      return new Date((u as User & { created_at?: string }).created_at || 0).getTime();
  }
}

function sortUsers(users: User[]): User[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...users].sort((a, b) => {
    const va = sortValue(a, sortKey);
    const vb = sortValue(b, sortKey);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
}

export async function renderAdminUsers(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'admin') {
    root.innerHTML = renderForbidden();
    return;
  }

  root.innerHTML = `
    <div class="simple-page">
      <h1 class="simple-page-title">${escapeHtml(t('view_admin_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('view_admin_sub'))}</div>
      <div class="admin-tabs-bar" id="adminTabs">
        ${TABS.map(
          (tab) => `
          <button class="admin-tab-btn ${tab === activeTab ? 'active' : ''}" data-tab="${tab}">
            ${escapeHtml(t(`view_admin_tab_${tab}`))}
          </button>`,
        ).join('')}
      </div>
      <div id="adminUserTable">${escapeHtml(t('view_admin_loading'))}</div>
    </div>
  `;

  // Tab switching.
  root.querySelectorAll<HTMLButtonElement>('#adminTabs .admin-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.tab as UserStatusFilter;
      if (!tab || tab === activeTab) return;
      activeTab = tab;
      void renderAdminUsers(root);
    });
  });

  await refreshTable(root);
}

async function refreshTable(root: HTMLElement): Promise<void> {
  const tbl = root.querySelector<HTMLDivElement>('#adminUserTable')!;
  tbl.textContent = t('view_admin_loading');
  try {
    const users = await listUsers({ status: activeTab });
    if (!users.length) {
      tbl.innerHTML = `<div class="placeholder-card">${escapeHtml(t('view_admin_empty'))}</div>`;
      return;
    }
    const sorted = sortUsers(users);
    const headerCells = SORT_COLS.map((col) => {
      const active = col.key === sortKey;
      const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
      return `<th class="sortable${active ? '' : ' inactive'}" data-sort="${col.key}">${escapeHtml(t(col.label))}<span class="sort-arrow">${arrow}</span></th>`;
    }).join('');
    tbl.innerHTML = `
      <div class="listing-table-wrap">
      <table class="listing-table">
        <thead>
          <tr>
            ${headerCells}
            <th>${escapeHtml(t('view_admin_th_actions'))}</th>
          </tr>
        </thead>
        <tbody>
          ${sorted.map(renderRow).join('')}
        </tbody>
      </table>
      </div>
    `;

    // Header click → toggle sort direction (or switch column).
    tbl.querySelectorAll<HTMLTableCellElement>('th.sortable').forEach((th) => {
      th.addEventListener('click', () => {
        const key = th.dataset.sort as SortKey;
        if (key === sortKey) {
          sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          sortKey = key;
          sortDir = 'asc';
        }
        void refreshTable(root);
      });
    });

    // Keep a map of id→user for the edit panel.
    const userMap = new Map<number, User>(users.map((u) => [u.id, u]));

    // Audit v2 virhe 7+8 (2026-05-30): edit opens a modal overlay rather
    // than inserting an inline row, and the modal includes the same
    // region-checklist that the "Uusi käyttäjä" form uses (audit virhe 8).
    // Inline-rivi vei näkymän koko leveyden ja jätti maakuntavalinnan
    // pois; modaali pitää näkymän siistinä ja yhtenäisenä uuden käyttäjän
    // luomisen kanssa.
    tbl.querySelectorAll<HTMLButtonElement>('.btn-edit').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.id);
        const user = userMap.get(id);
        if (!user) return;
        openEditUserModal(user, root);
      });
    });

    tbl.querySelectorAll<HTMLButtonElement>('.btn-approve').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.dataset.id);
        const lvl = Number(btn.dataset.level) || 2;
        btn.disabled = true;
        try {
          await approveUser(id, { level: lvl });
          await refreshTable(root);
        } catch (e) {
          alert(formatErr(e as Error));
        } finally {
          btn.disabled = false;
        }
      });
    });
    tbl.querySelectorAll<HTMLButtonElement>('.btn-reject').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.dataset.id);
        if (!confirm(t('view_admin_btn_reject') + '?')) return;
        btn.disabled = true;
        try {
          await rejectUser(id);
          await refreshTable(root);
        } catch (e) {
          alert(formatErr(e as Error));
        } finally {
          btn.disabled = false;
        }
      });
    });
  } catch (e) {
    tbl.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('view_admin_err'))}: ${escapeHtml(formatErr(e as Error))}</div>`;
  }
}

function renderRow(u: User): string {
  const created = (u as User & { created_at?: string }).created_at;
  const levelLabel = t(`level_${Math.max(0, Math.min(u.level || 0, 4))}`);
  const status = u.status || 'active';
  return `
    <tr data-user-id="${u.id}">
      <td>${escapeHtml(u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—')}</td>
      <td>${escapeHtml(u.email)}</td>
      <td>${escapeHtml(u.role || 'user')}</td>
      <td>${escapeHtml(levelLabel)}</td>
      <td><span class="user-status-pill user-status-${escapeHtml(status)}">${escapeHtml(status)}</span></td>
      <td>${escapeHtml(formatDate(created))}</td>
      <td>
        ${status === 'pending'
          ? `<button class="user-row-approve btn-approve" data-id="${u.id}" data-level="${u.level || 2}">${escapeHtml(t('view_admin_btn_approve'))}</button>
             <button class="user-row-reject btn-reject" data-id="${u.id}">${escapeHtml(t('view_admin_btn_reject'))}</button>`
          : ''}
        <button class="user-row-edit btn-edit" data-id="${u.id}">${escapeHtml(t('view_admin_btn_edit'))}</button>
      </td>
    </tr>
  `;
}

// ---------------------------------------------------------------------------
// Edit-user modal (audit v2 virhe 7 + 8, 2026-05-30)
//
// The modal mirrors the structure of "Uusi käyttäjä" in admin-panel.ts so
// admin sees a consistent layout when creating vs. editing. Region-
// checklist is shown only for level=1 ("Taso 1") accounts — the same
// rule as the create form. When the admin saves we PUT /api/v1/smt/admin/
// users/:id with the full payload including `allowed_regions: string[] |
// undefined`. (Korjaus 31.5.2026 / migraatio 023: aiempi label "Taso 1"
// mappautui virheellisesti DB level=0:aan = anonyymi BE:ssä.)
// ---------------------------------------------------------------------------

function openEditUserModal(u: User, root: HTMLElement): void {
  // Re-use a single overlay element so re-opening doesn't pile up DOM.
  let overlay = document.getElementById('editUserModal') as HTMLDivElement | null;
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'editUserModal';
    overlay.className = 'modal-overlay';
    document.body.appendChild(overlay);
  }
  overlay.innerHTML = editUserModalShell(u);
  overlay.style.display = 'flex';

  const close = (): void => {
    overlay!.style.display = 'none';
  };
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>('.modal-close')!.addEventListener('click', close);
  overlay.querySelector<HTMLButtonElement>('.btn-edit-cancel')!.addEventListener('click', close);

  const form = overlay.querySelector<HTMLFormElement>('#editUserForm')!;
  const msg = overlay.querySelector<HTMLDivElement>('#editUserMsg')!;

  // Delete user (asiakaspalaute 1.6 / referenssin editUserModal). Confirmed
  // destructive action; the backend refuses self-deletion with a 400.
  const delBtn = overlay.querySelector<HTMLButtonElement>('.btn-edit-delete');
  delBtn?.addEventListener('click', async () => {
    const label = u.name || u.email || `#${u.id}`;
    if (!confirm(t('admin_edit_delete_confirm', { name: label }))) return;
    delBtn.disabled = true;
    msg.className = 'modal-msg';
    msg.textContent = '';
    try {
      await deleteUser(u.id);
      msg.classList.add('ok');
      msg.textContent = t('admin_edit_delete_ok');
      setTimeout(() => {
        close();
        void refreshTable(root);
      }, 700);
    } catch (err) {
      msg.classList.add('err');
      msg.textContent = `${t('admin_edit_delete_err')}: ${formatErr(err as Error)}`;
      delBtn.disabled = false;
    }
  });
  const levelSel = form.querySelector<HTMLSelectElement>('select[name=level]')!;
  const regionsRow = form.querySelector<HTMLDivElement>('#editRegionsRow')!;

  // Region picker visibility: näytetään "Taso 1" -käyttäjille (DB level=1
  // = BE:n maakuntakohtainen). FE i18n korjattu 2026-05-31; aiempi off-
  // by-one ("Taso 1" = level=0) selitetty migraatio 023:ssa.
  const refreshRegions = (): void => {
    regionsRow.style.display = levelSel.value === '1' ? '' : 'none';
  };
  levelSel.addEventListener('change', refreshRegions);
  refreshRegions();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    msg.className = 'modal-msg';
    msg.textContent = '';
    const d = new FormData(form);
    const allowedRegions = Array.from(
      regionsRow.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked'),
    ).map((c) => c.value);
    const payload: UpdateUserInput = {
      level: Number(d.get('level')),
      role: String(d.get('role')) as UpdateUserInput['role'],
      status: String(d.get('status')) as UpdateUserInput['status'],
      organization: String(d.get('organization') || '').trim() || undefined,
      name: String(d.get('name') || '').trim() || undefined,
      email: String(d.get('email') || '').trim() || undefined,
      // Empty password = no change; only send when the admin typed one.
      password: String(d.get('password') || '').trim() || undefined,
      allowed_regions: levelSel.value === '1' ? allowedRegions : undefined,
    };
    const saveBtn = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
    saveBtn.disabled = true;
    try {
      await updateUser(u.id, payload);
      msg.classList.add('ok');
      msg.textContent = t('admin_edit_ok');
      setTimeout(() => {
        close();
        void refreshTable(root);
      }, 700);
    } catch (err) {
      msg.classList.add('err');
      msg.textContent = `${t('admin_edit_err')}: ${formatErr(err as Error)}`;
      saveBtn.disabled = false;
    }
  });
}

function editUserModalShell(u: User): string {
  const userLabel = u.name || u.email || `#${u.id}`;
  const allowed = new Set(u.allowed_regions || []);
  return `
    <div class="modal-card" style="max-width:640px">
      <div class="modal-header">
        <div class="modal-title">${escapeHtml(t('admin_edit_title', { name: userLabel }))}</div>
        <button class="modal-close" aria-label="close">×</button>
      </div>
      <div class="modal-body">
        <form id="editUserForm">
          <div class="form-row">
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_create_level'))}</label>
              <select class="admin-input" name="level">
                ${[0,1,2,3,4].map(n => `<option value="${n}" ${(u.level||0)===n?'selected':''}>${escapeHtml(t(`level_${n}_full`))}</option>`).join('')}
              </select>
            </div>
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_create_role'))}</label>
              <select class="admin-input" name="role">
                ${(['user','institution','admin'] as const).map(r => `<option value="${r}" ${(u.role||'user')===r?'selected':''}>${r}</option>`).join('')}
              </select>
            </div>
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_edit_status'))}</label>
              <select class="admin-input" name="status">
                ${(['active','pending','suspended','rejected'] as const).map(s => `<option value="${s}" ${(u.status||'active')===s?'selected':''}>${s}</option>`).join('')}
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_edit_name'))}</label>
              <input class="admin-input" name="name" value="${escapeHtml(u.name||'')}">
            </div>
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_edit_email'))}</label>
              <input class="admin-input" type="email" name="email" value="${escapeHtml(u.email||'')}">
            </div>
          </div>
          <div class="form-row">
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_edit_password'))} <span style="text-transform:none;color:var(--muted)">${escapeHtml(t('admin_edit_password_hint'))}</span></label>
              <input class="admin-input" type="text" name="password" placeholder="—" autocomplete="new-password">
            </div>
            <div class="form-field">
              <label class="form-label">${escapeHtml(t('admin_create_organization'))}</label>
              <input class="admin-input" name="organization" value="${escapeHtml(u.organization||'')}">
            </div>
          </div>
          <div class="form-row" id="editRegionsRow">
            <div class="form-field" style="flex:1">
              <label class="form-label">${escapeHtml(t('admin_create_regions'))}</label>
              <div class="region-checklist">
                ${REGIONS.map((r) => `
                  <label class="region-checkbox-label">
                    <input type="checkbox" value="${escapeHtml(r.name)}" ${allowed.has(r.name) ? 'checked' : ''}>
                    <span>${escapeHtml(r.name)}</span>
                  </label>`).join('')}
              </div>
            </div>
          </div>
          <div class="modal-actions" style="display:flex;gap:10px;flex-wrap:wrap">
            <button type="submit" class="admin-btn admin-btn-primary">${escapeHtml(t('admin_edit_save'))}</button>
            <button type="button" class="admin-btn admin-btn-danger btn-edit-delete">${escapeHtml(t('admin_edit_delete'))}</button>
            <button type="button" class="admin-btn admin-btn-secondary btn-edit-cancel">${escapeHtml(t('admin_edit_cancel'))}</button>
          </div>
          <div class="modal-msg" id="editUserMsg" style="margin-top:8px"></div>
        </form>
      </div>
    </div>
  `;
}

function renderForbidden(): string {
  return `
    <div class="simple-page">
      <div class="placeholder-card" style="color:rgb(140,55,55)">
        ${escapeHtml('Vain admin-tunnukset näkevät tämän sivun.')}
      </div>
    </div>
  `;
}

function formatDate(s: string | undefined | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
